/**
 * useAttributionSeed — seeds the three attribution toggles EXACTLY ONCE per create
 * dialog from integrations that are enabled AND configured; realtime snapshots never
 * re-seed, a touched toggle wins, edit/duplicate never seed (AC-021/022/061).
 *
 * `useAnalyticsSettings` resolves `loading` to `false` on TWO of its three paths: no
 * `projectId` (short-circuits to `DEFAULT_ANALYTICS_SETTINGS`, `integrations: []`) and
 * a missing analytics doc (`ProjectAnalyticsService.subscribeToSettings`'s `onSnapshot`
 * success callback fires with `!docSnap.exists()`, again `DEFAULT_ANALYTICS_SETTINGS`).
 * On the THIRD path — an `onSnapshot` listen/permission error — `subscribeToSettings`
 * (`src/modules/analytics-settings/services/ProjectAnalyticsService.ts:327-332`) only
 * logs the error and never invokes its callback at all, so `loading` can stay `true`
 * forever. That is a defect in `analytics-settings`, logged for its owner and NOT fixed
 * here (out of this module's boundary). Instead this hook adds a bounded wait: if
 * settings are still loading `SEED_SETTINGS_TIMEOUT_MS` after the dialog opens, treat
 * them as loaded with zero integrations (all toggles seed OFF once) rather than leaving
 * the dialog's `settingsLoaded` unresolved forever — never block the dialog. The timer
 * is armed per open-lifecycle (cleared on close/unmount) and cleared as soon as the
 * settings actually resolve, so it fires at most once per open and never re-seeds once
 * the real seed (or the timeout seed) has already happened.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAnalyticsSettings } from '@modules/analytics-settings/hooks/useAnalyticsSettings';
import { REQUIRED_CONFIG_FIELDS } from '@modules/analytics-settings/config/requiredConfigFields';
import { defaultToggles, type AttributionToggleState } from '@modules/webplacements/config/attributionPresets';
import { decideSeed } from '@modules/webplacements/config/attributionSeeding';

/** Bounded wait before treating stuck-loading settings as "loaded with zero integrations". */
export const SEED_SETTINGS_TIMEOUT_MS = 8000;

export interface UseAttributionSeedParams {
  open: boolean;
  mode: 'create' | 'edit';
  hasTemplate: boolean;
  projectId: string;
  onSeed: (toggles: AttributionToggleState) => void;
}

export interface UseAttributionSeedResult {
  markTouched: () => void;
  settingsLoaded: boolean;
}

export function useAttributionSeed({ open, mode, hasTemplate, projectId, onSeed }: UseAttributionSeedParams): UseAttributionSeedResult {
  const { loading, enabledIntegrations } = useAnalyticsSettings(projectId);
  const seededRef = useRef(false);
  const touchedRef = useRef(false);
  const [timedOut, setTimedOut] = useState(false);

  // Reset the per-dialog guards on close so a later re-open re-arms seeding.
  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      touchedRef.current = false;
      setTimedOut(false);
    }
  }, [open]);

  // Bounded wait for settings: armed while open AND still loading; cleared the moment
  // either condition stops holding (dialog closes, or settings actually resolve).
  useEffect(() => {
    if (!open || !loading) return undefined;
    const timer = setTimeout(() => setTimedOut(true), SEED_SETTINGS_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [open, loading]);

  useEffect(() => {
    const settingsLoaded = !loading || timedOut;
    const shouldSeed = decideSeed({
      open, mode, hasTemplate, settingsLoaded, seeded: seededRef.current, touched: touchedRef.current,
    });
    if (!shouldSeed) return;
    seededRef.current = true;
    onSeed(defaultToggles(enabledIntegrations, REQUIRED_CONFIG_FIELDS));
  }, [open, mode, hasTemplate, loading, timedOut, enabledIntegrations, onSeed]);

  const markTouched = useCallback(() => {
    touchedRef.current = true;
  }, []);

  return { markTouched, settingsLoaded: !loading || timedOut };
}
